import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Menu,
  MenuItem,
  Checkbox,
  Divider,
  List,
  ListItemButton,
  Grid,
  InputAdornment,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  ListItem,
  useTheme,
  useMediaQuery,
  Pagination,
  Autocomplete,
  CircularProgress,
  Skeleton,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  WarningAmber as WarningIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CloudDownload as CloudDownloadIcon,
  InfoOutlined as InfoOutlinedIcon,
  FiberNew as FiberNewIcon,
  Business as BusinessIcon,
  Link as LinkIcon,
  SwapVert as SwapVertIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { DataContext } from "../contexts/DataProvider";
import { fetchAllClients, createClient } from "../services/clients.service";
import { linkContact } from "../services/relationships.service";
import {
  fetchOrgContacts,
  fetchClientsByContact as fetchClientsByContactRequest,
  fetchContactById,
  deleteContact,
} from "../services/contacts.service";
import { createClientGraph } from "../functions/knowledgeGraphApi";
import { useAlert } from "../functions/useAlert";
import Container from "../base/Container";
import NoDataFound from "../components/common/NoDataFound";
import EmptyState from "../components/common/EmptyState";
import TableSkeleton from "../components/common/TableSkeleton";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ContactModal from "../components/modals/ContactModal";
import ImportContactDialog from "../components/common/ImportContactDialog";
import { getContactConfig } from "../utils/contactConfig";
import { useCRMSort, sortByCRMPref, CRM_SORT_DEFAULT, CRM_SORT_OPTIONS } from "../hooks/useCRMSort";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import { useSessionState } from "../hooks/useSessionState";
import ResizeHandle from "../components/common/ResizeHandle";
import { useResizableListPanel } from "../hooks/useResizableListPanel";

const LINK_ENTITY_TYPES = ["Individual", "SoleTrader", "Company", "Family", "Trust"];

const LinkToClientModal: React.FC<{
  open: boolean;
  onClose: () => void;
  contactId: string;
  onSuccess: () => void;
}> = ({ open, onClose, contactId, onSuccess }) => {
  const { login } = useContext(DataContext);
  const [setAlert] = useAlert();
  const [isSaving, setIsSaving] = useState(false);
  const [allClients, setAllClients] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [mode, setMode] = useState<"none" | "existing" | "new">("none");
  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [newClientName, setNewClientName] = useState("");
  const [newClientEntityType, setNewClientEntityType] = useState("Individual");
  const [relationshipType, setRelationshipType] = useState("Other");

  useEffect(() => {
    if (open) {
      setMode("none");
      setSelectedClient(null);
      setNewClientName("");
      setNewClientEntityType("Individual");
      setRelationshipType("Other");
      setClientsLoaded(false);
      setAllClients([]);
    }
  }, [open]);

  const loadAllClients = useCallback(async () => {
    if (clientsLoaded || loadingClients) return;
    setLoadingClients(true);
    try {
      const resp: any = await fetchAllClients();
      setAllClients(Array.isArray(resp) ? resp : []);
      setClientsLoaded(true);
    } catch {
      // silently fail
    } finally {
      setLoadingClients(false);
    }
  }, [clientsLoaded, loadingClients]);

  const handleConfirm = async () => {
    if (mode === "none") return setAlert("Please select or create a client to link.", "warning");
    if (mode === "new" && !newClientName.trim()) return setAlert("Client name is required.", "warning");

    setIsSaving(true);
    try {
      let linkedClientId: string | null = null;
      if (mode === "new") {
        const clientResp: any = await createClient({
          name: newClientName.trim(),
          entityType: newClientEntityType,
          orgId: login.data.orgId,
          userId: login.data.id,
          createdBy: login.data.id,
          profile: { clientStatus: "Active" },
        });
        if (clientResp.error) throw new Error(clientResp.error || "Failed to create client.");
        linkedClientId = clientResp._id || clientResp.id;
        if (linkedClientId) {
          createClientGraph(
            { _id: linkedClientId, name: clientResp.name, entityType: clientResp.entityType,
              orgId: clientResp.orgId || login.data.orgId, contactId: clientResp.contactId,
              address: clientResp.address || {}, profile: clientResp.profile || {} },
            { userId: login.data.id, orgId: login.data.orgId }
          ).catch((err) => console.error("createClientGraph failed:", err));
        }
      } else {
        linkedClientId = selectedClient?._id || selectedClient?.id || null;
      }
      if (!linkedClientId) throw new Error("No client ID returned.");
      await linkContact({
        clientId: linkedClientId,
        contactId,
        relationshipType,
        isPrimary: false,
        orgId: login.data.orgId,
        userId: login.data.id,
      });
      setAlert("Client linked successfully!", "success");
      onSuccess();
      onClose();
    } catch (error: any) {
      setAlert(error?.message || "Failed to link client.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <BusinessIcon color="primary" fontSize="small" />
        Link to Client
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Mode toggle buttons */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant={mode === "new" ? "contained" : "outlined"}
              startIcon={<AddIcon sx={{ fontSize: "0.9rem !important" }} />}
              onClick={() => {
                if (mode === "new") {
                  setMode("none");
                  setNewClientName("");
                  setNewClientEntityType("Individual");
                  setRelationshipType("Other");
                } else {
                  setMode("new");
                  setSelectedClient(null);
                  setRelationshipType(getContactConfig("Individual").defaultRelationship);
                }
              }}
              sx={{ flex: 1, py: 0.4, fontSize: "0.75rem" }}
            >
              New Client
            </Button>
            <Button
              size="small"
              variant={mode === "existing" ? "contained" : "outlined"}
              startIcon={<LinkIcon sx={{ fontSize: "0.9rem !important" }} />}
              onClick={() => {
                if (mode === "existing") {
                  setMode("none");
                  setSelectedClient(null);
                  setRelationshipType("Other");
                } else {
                  loadAllClients();
                  setMode("existing");
                  setNewClientName("");
                  setNewClientEntityType("Individual");
                  setRelationshipType("Other");
                }
              }}
              sx={{ flex: 1, py: 0.4, fontSize: "0.75rem" }}
            >
              Link Existing
            </Button>
          </Box>

          {/* New client form */}
          {mode === "new" && (
            <Grid container spacing={1.5}>
              <Grid item xs={8}>
                <TextField
                  required
                  label="Client Name"
                  fullWidth
                  size="small"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={newClientEntityType}
                    label="Type"
                    onChange={(e) => {
                      setNewClientEntityType(e.target.value);
                      setRelationshipType(getContactConfig(e.target.value).defaultRelationship);
                    }}
                  >
                    {LINK_ENTITY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Relationship Type</InputLabel>
                  <Select
                    value={relationshipType}
                    label="Relationship Type"
                    onChange={(e) => setRelationshipType(e.target.value)}
                  >
                    {getContactConfig(newClientEntityType).relationships.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Link existing client form */}
          {mode === "existing" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Autocomplete
                options={allClients}
                loading={loadingClients}
                getOptionLabel={(opt) => opt.name || ""}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                value={selectedClient}
                onChange={(_, val) => {
                  setSelectedClient(val);
                  setRelationshipType(val ? getContactConfig(val.entityType).defaultRelationship : "Other");
                }}
                filterOptions={(opts, { inputValue }) => {
                  if (!inputValue) return opts;
                  const q = inputValue.toLowerCase();
                  return opts.filter((o) => o.name?.toLowerCase().includes(q));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search clients"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>{loadingClients ? <CircularProgress size={14} /> : null}{params.InputProps.endAdornment}</>
                      ),
                    }}
                  />
                )}
                renderOption={(props, opt) => (
                  <Box component="li" {...props} key={opt._id}>
                    <Box>
                      <Typography variant="body2">{opt.name}</Typography>
                      {opt.entityType && (
                        <Typography variant="caption" color="text.secondary">{opt.entityType}</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              />
              {selectedClient && (
                <FormControl fullWidth size="small">
                  <InputLabel>Relationship Type</InputLabel>
                  <Select
                    value={relationshipType}
                    label="Relationship Type"
                    onChange={(e) => setRelationshipType(e.target.value)}
                  >
                    {getContactConfig(selectedClient?.entityType).relationships.map((r) => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={isSaving}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={isSaving || mode === "none"}>
          {isSaving ? <CircularProgress size={20} color="inherit" /> : "Link Client"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StatCard = ({ title, value, icon }) => (
  <Paper
    elevation={2}
    sx={{
      p: 2,
      display: "flex",
      alignItems: "center",
      borderRadius: "12px",
      height: "100%",
    }}
  >
    <Box sx={{ mr: 2, color: "primary.main" }}>{icon}</Box>
    <Box>
      <Typography color="text.secondary">{title}</Typography>
      <Typography variant="h5" fontWeight="bold">
        {value}
      </Typography>
    </Box>
  </Paper>
);

const ContactDetailPanel = ({
  contact,
  linkedClients,
  isLoading,
  isRestoringSelection,
  onOpenModal,
  onLinkClient,
}: {
  contact: any;
  linkedClients: any[];
  isLoading: boolean;
  // True for the split second between mount and a persisted selection being resolved against
  // the freshly-fetched contact list — shows a loading placeholder instead of "Select a
  // Contact", which would otherwise flash before the previous selection reappears.
  isRestoringSelection?: boolean;
  onOpenModal: (type: string, contact?: any) => void;
  onLinkClient: () => void;
}) => {
  if (!contact) {
    if (isRestoringSelection) {
      return (
        <Paper sx={{ height: "100%", p: 3, borderRadius: "16px" }}>
          <Skeleton variant="text" width="35%" height={40} />
          <Skeleton variant="rounded" width={90} height={24} sx={{ mt: 1 }} />
          <Divider sx={{ my: 2 }} />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="60%" />
        </Paper>
      );
    }

    return (
      <Paper
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          p: 3,
          borderRadius: "12px",
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 60, color: "text.secondary" }} />
        <Typography variant="h6" mt={2}>
          Select a Contact
        </Typography>
        <Typography color="text.secondary">
          Choose a contact from the list to view their details, linked clients,
          and more.
        </Typography>
      </Paper>
    );
  }

  const { admin, kyc } = contact;

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: "16px",
        height: "100%", // Forces panel to fit parent container
        display: "flex",
        flexDirection: "column",
        overflow: "hidden" // Prevents the paper itself from scrolling
      }}
    >
      {/* FIXED TOP SECTION: Header & Details */}
      <Box flexShrink={0}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {contact.name}
            </Typography>
            <Stack direction="row" spacing={1} mt={1} mb={2}>
              <Chip
                label={contact.source || "Manual"}
                size="small"
                color={contact.source === 'Monday.com' || contact.source === 'WorkSorted' ? 'primary' : 'default'}
                variant="outlined"
              />
            </Stack>

            <Stack spacing={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2">{contact.email}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {contact.phone || "No phone number"}
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Box>
            <Tooltip title="Edit Contact">
              <IconButton
                size="small"
                onClick={() => onOpenModal("edit", contact)}
              >
                <EditIcon color="primary" fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Contact">
              <IconButton
                size="small"
                onClick={() => onOpenModal("delete", contact)}
              >
                <DeleteIcon color="error" fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600}>
              Profile
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            {admin && (admin.title || admin.jobTitle) ? (
              <Stack spacing={0.5}>
                {admin.title && (
                  <Typography variant="body2" color="text.secondary">
                    Title:
                    <Typography component="span" variant="body2" color="text.primary" ml={1}>
                      {admin.title}
                    </Typography>
                  </Typography>
                )}
                {admin.jobTitle && (
                  <Typography variant="body2" color="text.secondary">
                    Job Title:
                    <Typography component="span" variant="body2" color="text.primary" ml={1}>
                      {admin.jobTitle}
                    </Typography>
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.disabled">
                No administrative details
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            {kyc && (kyc.dob || kyc.employmentStatus || kyc.annualIncome || kyc.targetRetirementAge || kyc.idDocType) ? (
              <Stack spacing={0.5}>
                {kyc.dob && (
                  <Typography variant="body2" color="text.secondary">
                    DOB:
                    <Typography component="span" variant="body2" color="text.primary" ml={1}>
                      {new Date(kyc.dob).toLocaleDateString('en-AU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Typography>
                )}
                {kyc.employmentStatus && (
                  <Typography variant="body2" color="text.secondary">
                    Employment:
                    <Typography component="span" variant="body2" color="text.primary" ml={1}>
                      {kyc.employmentStatus}
                    </Typography>
                  </Typography>
                )}
                {kyc.annualIncome && (
                  <Typography variant="body2" color="text.secondary">
                    Income:
                    <Typography component="span" variant="body2" color="text.primary" ml={1}>
                      {kyc.annualIncome}
                    </Typography>
                  </Typography>
                )}
                {kyc.targetRetirementAge && (
                  <Typography variant="body2" color="text.secondary">
                    Retirement Age:
                    <Typography component="span" variant="body2" color="text.primary" ml={1}>
                      {kyc.targetRetirementAge}
                    </Typography>
                  </Typography>
                )}
                {kyc.idDocType && (
                  <Typography variant="body2" color="text.secondary">
                    ID Type:
                    <Typography component="span" variant="body2" color="text.primary" ml={1}>
                      {kyc.idDocType}
                    </Typography>
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.disabled">
                No KYC information
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Added on {new Date(contact.createdAt).toLocaleDateString()}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
      </Box>

      {/* SCROLLABLE BOTTOM SECTION: Linked Clients */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0, // Critical to allow this flex item to shrink and scroll
      }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">Linked Clients</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onLinkClient}
            sx={{ fontSize: "0.75rem", py: 0.4 }}
          >
            Link to Client
          </Button>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: "auto", pr: 1 }}>
          {isLoading ? (
            <TableSkeleton rows={3} cols={2} rowHeight={40} />
          ) : linkedClients.length > 0 ? (
            <List dense sx={{ p: 0 }}>
              {linkedClients.map((client) => (
                <ListItem key={client._id} disablePadding>
                  <ListItemText
                    primary={client.name}
                    secondary={client.relationshipType ? `Relationship: ${client.relationshipType}` : 'Linked'}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="50%">
              <Typography variant="body2" color="text.secondary" align="center">
                Not linked to any clients.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const ContactsPage = () => {
  const { login } = useContext(DataContext);
  const [allContacts, setAllContacts] = useState<any[]>([]);
  const [isAllContactsLoading, setIsAllContactsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useSessionState("contacts:searchQuery", "");
  const [setAlert] = useAlert();

  // selectedContactId is persisted; selectedContact itself is re-resolved against
  // allContacts once loaded (same pattern as ClientsPage's selectedClientId).
  const [selectedContactId, setSelectedContactId] = useSessionState<string | null>("contacts:selectedContactId", null);
  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  // Restoring selectedContact from selectedContactId is a one-shot "resume where I left off"
  // action for this mount — see the matching guard + no-op-on-reclick fix in ClientsPage.tsx.
  const hasRestoredSelectionRef = useRef(false);
  // True only while there's a persisted selection still waiting to be resolved against the
  // freshly-fetched contact list — see the matching flag in ClientsPage.tsx.
  const [isRestoringSelection, setIsRestoringSelection] = useState(() => !!selectedContactId);
  const [contactForModal, setContactForModal] = useState<any | null>(null);
  const [linkedClients, setLinkedClients] = useState<any[]>([]);
  const [isLinkedClientsLoading, setIsLinkedClientsLoading] = useState(false);

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isDeleteContactDialogOpen, setIsDeleteContactDialogOpen] =
    useState(false);
  const [isImportContactModalOpen, setIsImportContactModalOpen] =
    useState(false);
  const [isLinkClientModalOpen, setIsLinkClientModalOpen] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);

  // --- PAGINATION STATE & HOOKS ---
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  // Below md the list and detail panes stack vertically, so there is nothing to drag.
  const isStacked = useMediaQuery(theme.breakpoints.down('md'));
  const {
    width: listWidth,
    panelRef: listPanelRef,
    handleResizeStart,
  } = useResizableListPanel({
    storageKey: "contacts:listWidth",
    defaultWidth: 420,
    minWidth: 300,
    maxWidth: 720,
  });
  const [contactPage, setContactPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sourceFilter, setSourceFilter] = useSessionState("contacts:sourceFilter", "All");

  const [contactSortPref, setContactSortPref] = useCRMSort();
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(null);

  // Reset pagination when search or filter changes
  useEffect(() => {
    setContactPage(1);
  }, [searchQuery, sourceFilter, contactSortPref]);

  useEffect(() => {
    localStorage.setItem("prospoContactSortPref", JSON.stringify(contactSortPref));
  }, [contactSortPref]);

  useEffect(() => {
    if (allContacts.length === 0) return;
    if (selectedContact) {
      const freshData = allContacts.find((c) => c._id === selectedContact._id);
      if (freshData && freshData !== selectedContact) {
        setSelectedContact(freshData);
      }
      return;
    }
    if (hasRestoredSelectionRef.current) return;
    hasRestoredSelectionRef.current = true;
    setIsRestoringSelection(false);
    if (selectedContactId) {
      const restored = allContacts.find((c) => c._id === selectedContactId);
      if (restored) {
        setSelectedContact(restored);
        fetchClientsByContact(restored._id);
      } else {
        setSelectedContactId(null); // stale id (e.g. contact since deleted)
      }
    }
  }, [allContacts, selectedContact, selectedContactId]);

  const fetchAllContactsData = useCallback(async () => {
    setIsAllContactsLoading(true);
    try {
      const response: any = await fetchOrgContacts(login.data.orgId);
      setAllContacts(response.contacts || []);
    } catch (error) {
      setAlert("Failed to load contacts.", "error");
    } finally {
      setIsAllContactsLoading(false);
    }
  }, [login.data.orgId, setAlert]);

  useEffect(() => {
    fetchAllContactsData();
  }, [fetchAllContactsData]);

  // Listen for AI-triggered modal requests
  useEffect(() => {
    const handleAIModalRequest = (event: any) => {
      const data = event.detail || event;
      const modalName = data.modalName || data.targetModal;
      const params = data.params || data.modalParams || data.initialData;

      if (modalName === 'ContactModal') {
        // Check if this is edit mode and we need to fetch contact data
        if (params?.mode === 'edit' && params?.contactId) {
          // Fetch full contact data for editing
          fetchContactDataAndOpenEditModal(params.contactId, params.contactName);
        } else {
          // Create mode or we already have contact data
          setContactForModal(params);
          setIsContactModalOpen(true);
        }
      } else if (modalName === 'ImportContactDialog') {
        setIsImportContactModalOpen(true);
      }
    };

    window.addEventListener('ai-open-modal', handleAIModalRequest);

    return () => {
      window.removeEventListener('ai-open-modal', handleAIModalRequest);
    };
  }, []);

  const fetchClientsByContact = async (contactId: string) => {
    if (!contactId) {
      setLinkedClients([]);
      return;
    }
    setIsLinkedClientsLoading(true);
    try {
      const response: any = await fetchClientsByContactRequest(contactId);
      setLinkedClients(response.data || []);
    } catch (error) {
      setLinkedClients([]);
    } finally {
      setIsLinkedClientsLoading(false);
    }
  };

  // Function to fetch full contact data and open edit modal (for AI-triggered edits)
  const fetchContactDataAndOpenEditModal = async (contactId: string, contactName?: string) => {
    try {
      const response: any = await fetchContactById(contactId);
      if (response && response._id) {
        setContactForModal(response);
        setIsContactModalOpen(true);
      } else {
        console.error('Failed to fetch contact data for editing');
        // Fallback: create a minimal contact object
        setContactForModal({
          _id: contactId,
          name: contactName || 'Unknown Contact'
        });
        setIsContactModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching contact data:', error);
      // Fallback: create a minimal contact object
      setContactForModal({
        _id: contactId,
        name: contactName || 'Unknown Contact'
      });
      setIsContactModalOpen(true);
    }
  };

  const handleSelectContact = (contact: any) => {
    // Clicking the already-selected row is a no-op, not a toggle — see ClientsPage.tsx.
    if (selectedContact?._id === contact._id) return;
    setSelectedContact(contact);
    setSelectedContactId(contact._id);
    fetchClientsByContact(contact._id);
  };


  const filteredContacts = useMemo(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();

    const filtered = allContacts.filter((contact) => {
      const matchesSearch =
        !trimmedQuery ||
        contact.name.toLowerCase().includes(trimmedQuery) ||
        contact.email.toLowerCase().includes(trimmedQuery);

      const contactSource = contact.source || "Manual";
      const matchesSource =
        sourceFilter === "All" || contactSource === sourceFilter;

      return matchesSearch && matchesSource;
    });

    return sortByCRMPref(filtered, contactSortPref);
  }, [allContacts, searchQuery, sourceFilter, contactSortPref]);

  // --- PAGINATION LOGIC ---
  const paginatedContacts = useMemo(
    () =>
      filteredContacts.slice(
        (contactPage - 1) * rowsPerPage,
        contactPage * rowsPerPage
      ),
    [contactPage, filteredContacts, rowsPerPage]
  );

  // Use paginated or full list based on screen size
  const displayedContacts = isSmallScreen ? paginatedContacts : filteredContacts;
  // ------------------------

  const stats = useMemo(() => {
    const startOfMonth = dayjs().startOf("month");

    const mondayContacts = allContacts.filter(
      (c) => c.source === "Monday.com"
    ).length;

    const worksortedContacts = allContacts.filter(
      (c) => c.source === "WorkSorted"
    ).length;

    const adviserLogicContacts = allContacts.filter(
      (c) => c.source === "AdviserLogic"
    ).length;

    const outlookContacts = allContacts.filter(
      (c) => c.source === "Microsoft Outlook"
    ).length;

    const newThisMonth = allContacts.filter((c) =>
      dayjs(c.createdAt).isAfter(startOfMonth)
    ).length;

    return {
      total: allContacts.length,
      mondayContacts,
      worksortedContacts,
      adviserLogicContacts,
      outlookContacts,
      newThisMonth,
    };
  }, [allContacts]);

  const handleOpenModal = (type: string, contact?: any) => {
    setContactForModal(contact);

    if (type === "edit") {
      setContactForModal(contact);
      setIsContactModalOpen(true);
    }

    if (type === "delete") setIsDeleteContactDialogOpen(true);
  };

  const handleAddContact = (newContact: any) => {
    fetchAllContactsData();
    setSelectedContact(newContact);
    fetchClientsByContact(newContact._id);
  };

  const handleSaveContact = (updatedContact: any) => {
    fetchAllContactsData();
    if (selectedContact?._id === updatedContact._id) {
      setSelectedContact(updatedContact);
      fetchClientsByContact(updatedContact._id);
    }
  };

  const handleDeleteContact = async () => {
    if (!contactForModal) return;
    try {
      if (selectedContactIds.length > 1) {
        await Promise.all(
          selectedContactIds.map((contactId) => deleteContact(contactId))
        );
        setAlert(`${selectedContactIds.length} contacts deleted successfully`, "success");
        setSelectedContactIds([]);
      } else if (selectedContactIds.length === 1) {
        await deleteContact(selectedContactIds[0]);
        setAlert("Contact deleted successfully", "success");
        setSelectedContactIds([]);
      } else {
        await deleteContact(contactForModal._id);
        setAlert("Contact deleted successfully", "success");
      }

      fetchAllContactsData();
      setSelectedContact(null);
      setLinkedClients([]);
    } catch (error) {
      setAlert("Failed to delete contact(s)", "error");
    } finally {
      setIsDeleteContactDialogOpen(false);
      setContactForModal(null);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);

  const convertToCSV = (arr) => {
    if (!arr || arr.length === 0) return "";
    const keys = Object.keys(arr[0]);
    const header = keys.join(",");
    const rows = arr.map((row) =>
      keys.map((key) => JSON.stringify(row[key])).join(",")
    );
    return [header, ...rows].join("\n");
  };

  const handleExportContacts = (idsToExport: string[]) => {
    handleMenuClose();
    if (idsToExport.length === 0) {
      return setAlert(
        "Please select at least one contact to export.",
        "warning"
      );
    }
    const dataToExport = allContacts
      .filter((c) => idsToExport.includes(c._id))
      .map(({ _id, name, email, phone, source }) => ({
        _id,
        name,
        email,
        phone,
        source,
      }));

    const csvData = convertToCSV(dataToExport);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "contacts.csv");
  };

  return (
    <Container>
      <Box sx={{ height: "calc(100vh - 118px)", overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: "space-between",
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography variant="h4" fontWeight={700}>
            Contact Management
          </Typography>
          {/* Import Button - Secondary style */}
          <Box sx={{
            display: 'flex',
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center'
          }}>
            {/* Import Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsImportContactModalOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Import Contacts
            </Button>

            {/* Export Button - Secondary style */}
            {/* <Button
              variant="outlined"
              size="small"
              onClick={handleMenuOpen}
              endIcon={<ArrowDropDownIcon />}
            >
              Export Contacts
            </Button> */}

            {/* Export Menu */}
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleExportContacts(selectedContactIds);
                }}
                disabled={selectedContactIds.length === 0}
              >
                Export Selected ({selectedContactIds.length})
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleExportContacts(allContacts.map((c) => c._id));
                }}
              >
                Export All
              </MenuItem>
            </Menu>


            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setContactForModal(null);
                setIsContactModalOpen(true);
              }}
              startIcon={<AddIcon />}
              sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}
            >
              Add Contact
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Total Contacts"
              value={stats.total}
              icon={<PeopleIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Monday.com"
              value={stats.mondayContacts}
              icon={<CloudDownloadIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="WorkSorted"
              value={stats.worksortedContacts}
              icon={<CloudDownloadIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="AdviserLogic"
              value={stats.adviserLogicContacts}
              icon={<CloudDownloadIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="New This Month"
              value={stats.newThisMonth}
              icon={<FiberNewIcon />}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 0 },
            height: { xs: "auto", md: "calc(100vh - 258px)" },
          }}
        >
          <Box
            ref={listPanelRef}
            sx={{
              display: "flex",
              flexDirection: "column",
              // Fixed, user-draggable width once side-by-side; full width when stacked.
              // The drag writes an inline width straight onto this element, so when the
              // layout stacks we must override it back to 100% (inline styles beat sx).
              width: isStacked ? "100% !important" : `${listWidth}px`,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: "12px",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                height: "calc(100vh - 308px)",
                minHeight: { xs: '500px', md: 'auto' },
              }}
            >
              <Stack direction="row" spacing={1} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Source</InputLabel>
                  <Select
                    value={sourceFilter}
                    label="Source"
                    onChange={(e) => setSourceFilter(e.target.value)}
                  >
                    {["All", "Manual", "Monday.com", "WorkSorted", "AdviserLogic", "Microsoft Outlook"].map(
                      (type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
                <Tooltip title="Sort contacts">
                  <IconButton
                    size="small"
                    onClick={(e) => setSortMenuAnchorEl(e.currentTarget)}
                    color={
                      contactSortPref.field !== CRM_SORT_DEFAULT.field ||
                      contactSortPref.order !== CRM_SORT_DEFAULT.order
                        ? "primary"
                        : "default"
                    }
                  >
                    <SwapVertIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={sortMenuAnchorEl}
                  open={Boolean(sortMenuAnchorEl)}
                  onClose={() => setSortMenuAnchorEl(null)}
                >
                  {CRM_SORT_OPTIONS.map((option) => (
                    <MenuItem
                      key={`${option.field}-${option.order}`}
                      selected={
                        contactSortPref.field === option.field &&
                        contactSortPref.order === option.order
                      }
                      onClick={() => {
                        setContactSortPref({ field: option.field, order: option.order });
                        setSortMenuAnchorEl(null);
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Stack>
              {/* Header bar — outside scroll; pr hard-coded to 8px + 15px scrollbar so icons align with rows */}
              {!isAllContactsLoading && displayedContacts.length > 0 && (
                <Box
                  display="flex"
                  alignItems="center"
                  py={1}
                  pl={1}
                  sx={{
                    pr: "16px !important",
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    mb: 1,
                    gap: 0.5,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={
                      displayedContacts.length > 0 &&
                      selectedContactIds.length === filteredContacts.length
                    }
                    indeterminate={
                      selectedContactIds.length > 0 &&
                      selectedContactIds.length < filteredContacts.length
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setSelectedContactIds(filteredContacts.map((c) => c._id));
                      } else {
                        setSelectedContactIds([]);
                      }
                    }}
                  />
                  <Box flexGrow={1} px={1}>
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                      {selectedContactIds.length} of {filteredContacts.length} selected
                    </Typography>
                  </Box>

                  {/* Always 2 icon slots — matches row's Edit + Delete columns */}
                  <Box display="flex" gap={0.25} ml={0.5}>
                    {selectedContactIds.length > 0 ? (
                      <Tooltip title={`Export ${selectedContactIds.length} selected`}>
                        <IconButton size="small" color="primary" onClick={() => handleExportContacts(selectedContactIds)}>
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <IconButton size="small" disabled sx={{ visibility: "hidden", pointerEvents: "none" }}>
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    )}
                    {selectedContactIds.length > 0 ? (
                      <Tooltip title={`Delete ${selectedContactIds.length} selected`}>
                        <IconButton size="small" color="error" onClick={() => { setIsDeleteContactDialogOpen(true); setContactForModal({ name: `${selectedContactIds.length} contact(s)` } as any); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <IconButton size="small" disabled sx={{ visibility: "hidden", pointerEvents: "none" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}

              <Box sx={{ overflowY: "auto", flexGrow: 1, minHeight: 0 }}>
                {isAllContactsLoading ? (
                  <TableSkeleton rows={5} cols={5} />
                ) : allContacts.length === 0 ? (
                  <EmptyState
                    icon={<PersonOutlineIcon sx={{ fontSize: 48 }} />}
                    title="No contacts yet"
                    description="Contacts are usually added when creating a client."
                    primaryAction={{
                      label: '+ Add Standalone Contact',
                      onClick: () => { setContactForModal(null); setIsContactModalOpen(true); },
                    }}
                  />
                ) : displayedContacts.length > 0 ? (
                  <List disablePadding>
                      {displayedContacts.map((contact) => {
                        const isChecked = selectedContactIds.includes(contact._id);
                        return (
                        <ListItem
                          key={contact._id}
                          disablePadding
                          sx={{ mb: 0.5 }}
                        >
                          <ListItemButton
                            selected={selectedContact?._id === contact._id}
                            onClick={() => handleSelectContact(contact)}
                            sx={{ pr: 1 }}
                          >
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setSelectedContactIds((prev) => [...prev, contact._id]);
                                } else {
                                  setSelectedContactIds((prev) => prev.filter((id) => id !== contact._id));
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ p: 0.5, mr: 0.5 }}
                            />
                            <ListItemText
                              primary={contact.name}
                              secondary={contact.email}
                            />
                            {/* Actions — always visible */}
                            <Box display="flex" gap={0.25} flexShrink={0} ml={0.5}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleOpenModal("edit", contact); }}
                                >
                                  <EditIcon fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleOpenModal("delete", contact); }}
                                >
                                  <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </ListItemButton>
                        </ListItem>
                        );
                      })}
                    </List>
                ) : (
                  <NoDataFound
                    title="No Contacts Found"
                    subtitle="Try adjusting your filters or add a new contact."
                  />
                )}
              </Box>

              {/* --- PAGINATION CONTROLS (CONDITIONAL) --- */}
              {isSmallScreen && filteredContacts.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pt: 2,
                    mt: "auto",
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing{" "}
                    <strong>{filteredContacts.length === 0 ? 0 : (contactPage - 1) * rowsPerPage + 1}</strong>
                    {" "}to{" "}
                    <strong>{Math.min(contactPage * rowsPerPage, filteredContacts.length)}</strong>
                    {" "}of{" "}
                    <strong>{filteredContacts.length}</strong>
                    {" "}results
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                      Rows per page:
                    </Typography>
                    <FormControl size="small" variant="outlined" sx={{ minWidth: 61 }}>
                      <Select
                        value={rowsPerPage}
                        onChange={(e) => { setRowsPerPage(Number(e.target.value)); setContactPage(1); }}
                        displayEmpty
                        inputProps={{ "aria-label": "Rows per page" }}
                        sx={{ ".MuiOutlinedInput-notchedOutline": { border: 0 } }}
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      onClick={() => setContactPage((p) => Math.max(1, p - 1))}
                      disabled={contactPage === 1}
                    >
                      <NavigateBeforeIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setContactPage((p) => Math.min(Math.ceil(filteredContacts.length / rowsPerPage), p + 1))}
                      disabled={contactPage >= Math.ceil(filteredContacts.length / rowsPerPage)}
                    >
                      <NavigateNextIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}
              {/* ------------------------------------------- */}
            </Paper>
          </Box>

          {!isStacked && <ResizeHandle onMouseDown={handleResizeStart} gutter={9} />}

          <Box sx={{ flex: 1, minWidth: 0, height: { xs: "auto", md: "100%" } }}>
            <Box sx={{ pt: { xs: 2, md: 0 }, height: "100%" }}>
              <ContactDetailPanel
                contact={selectedContact}
                linkedClients={linkedClients}
                isLoading={isLinkedClientsLoading}
                isRestoringSelection={isRestoringSelection}
                onOpenModal={handleOpenModal}
                onLinkClient={() => setIsLinkClientModalOpen(true)}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <ContactModal
        open={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSave={() => {
          fetchAllContactsData();
          // If editing the currently selected contact, update the view
          if (selectedContact && contactForModal && selectedContact._id === contactForModal._id) {
            // Optionally refetch or just rely on the list update
            // fetchClientsByContact(selectedContact._id); 
          }
        }}
        contactData={contactForModal} // null = Add, Object = Edit
      />
      <ConfirmDeleteModal
        open={isDeleteContactDialogOpen}
        entityType="contact"
        entityName={contactForModal?.name ?? ''}
        onConfirm={handleDeleteContact}
        onCancel={() => setIsDeleteContactDialogOpen(false)}
      />
      {selectedContact && (
        <LinkToClientModal
          open={isLinkClientModalOpen}
          onClose={() => setIsLinkClientModalOpen(false)}
          contactId={selectedContact._id}
          onSuccess={() => fetchClientsByContact(selectedContact._id)}
        />
      )}
      <ImportContactDialog
        open={isImportContactModalOpen}
        onClose={() => setIsImportContactModalOpen(false)}
        onSave={fetchAllContactsData}
      // You might need to update ImportContactDialog to accept setIsContactModalOpen instead
      // or just remove this prop if the import dialog handles opening the modal internally differently
      // setIsAddContactModalOpen={setIsContactModalOpen}
      />
    </Container>
  );
};

export default ContactsPage;